import React, { useEffect, useState } from 'react';
import { useT } from '@/i18n/labels';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';

/** One selectable CRM category, as the server offers it. */
export interface CrmCategoryOptionDto {
  /** What the portal STORES — what a write must send. */
  value: string;
  /** What the portal DISPLAYS. Routinely differs from `value`. */
  label: string;
  /** The Botablex-side value it maps to, when the tenant authored a value map. */
  botablexValue?: string | null;
}

/** The picker's server state for one conversation. */
export interface CrmSessionCategoryDto {
  available: boolean;
  multiValue: boolean;
  options: CrmCategoryOptionDto[];
  selected: string[];
  hasTicket: boolean;
  reason?: string | null;
}

interface CrmCategoryPickerProps {
  /**
   * The conversation whose CRM ticket is being categorised.
   *
   * SESSION, not customer: a ticket exists per conversation and can exist for an anonymous
   * visitor with no customer record — so a customer-keyed picker would be unavailable for
   * exactly the conversations most likely to need categorising.
   */
  sessionId: string;
  /** Someone else owns this conversation (console v2): the choices are shown, not editable. */
  disabled?: boolean;
}

/**
 * The tenant's own CRM category on this conversation, set by a HUMAN agent.
 *
 * - A SECOND axis, independent of the console's lifecycle category. Botablex's
 *   `conversation_state_defs` drive the pipeline column; this is the tenant's CRM taxonomy,
 *   answering "what is this about" in their own words. They were deliberately kept separate.
 * - Values are the PORTAL's stored values, not their labels — in the live portal 5 of the 18
 *   category options differ, so a picker bound to labels would write something the CRM rejects.
 *   The label is shown; the value is sent.
 * - Every state is rendered explicitly rather than hidden. "Not mapped yet", "no ticket yet" and
 *   "the portal rejected it" need different actions from the agent, and a picker that silently
 *   disappears in all three cases teaches them not to trust it.
 */
export const CrmCategoryPicker: React.FC<CrmCategoryPickerProps> = ({ sessionId, disabled = false }) => {
  const { t } = useT();
  const [state, setState] = useState<CrmSessionCategoryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setState(null);

    (async () => {
      try {
        const res = await axiosClient.get<CrmSessionCategoryDto>(
          API.CRM.SESSION_CATEGORIES(sessionId),
        );
        if (!cancelled) setState(res.data);
      } catch {
        // Best-effort, like the rest of this panel: a CRM outage must not blank the contact card.
        if (!cancelled) setState(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const save = async (next: string[]) => {
    setSaving(true);
    setError(null);

    // Optimistic, with the previous selection kept so a rejection can restore it. The alternative
    // — waiting for the round trip — makes a dropdown feel broken on a slow portal.
    const previous = state?.selected ?? [];
    setState((s) => (s ? { ...s, selected: next } : s));

    try {
      const res = await axiosClient.put<CrmSessionCategoryDto>(
        API.CRM.SESSION_CATEGORIES(sessionId),
        { values: next },
      );
      setState(res.data);
    } catch {
      setState((s) => (s ? { ...s, selected: previous } : s));
      setError(t('ctx_crm_category_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  // Nothing configured for this tenant. Silent on purpose: an agent cannot act on it, and a
  // permanent "not configured" notice on every conversation is noise. The mapping screen is where
  // this gets fixed, and the server states the reason in its response for whoever is debugging.
  if (!state || !state.available) return null;

  const selected = new Set(state.selected);

  const toggle = (value: string) => {
    if (saving || disabled) return;

    if (!state.multiValue) {
      // Single-select: clicking the selected value clears it, which is the only way to unset a
      // category that was set by mistake.
      void save(selected.has(value) ? [] : [value]);
      return;
    }

    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    void save([...next]);
  };

  return (
    <div className="ac-ctx-sec" data-testid="ctx-crm-categories">
      <div className="ac-ctx-sec-title">{t('ctx_crm_category_title')}</div>

      {!state.hasTicket ? (
        // Distinct from "not configured": the mapping IS there, the conversation simply has no CRM
        // ticket yet. Saying so tells the agent it will become available rather than that it is broken.
        <div className="ac-ctx-na" data-testid="ctx-crm-categories-no-ticket">
          {t('ctx_crm_category_no_ticket')}
        </div>
      ) : (
        <>
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
            role={state.multiValue ? 'group' : 'radiogroup'}
            aria-label={t('ctx_crm_category_title')}
          >
            {state.options.map((opt) => {
              const on = selected.has(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  className="ac-chip"
                  onClick={() => toggle(opt.value)}
                  disabled={saving || disabled}
                  role={state.multiValue ? 'checkbox' : 'radio'}
                  aria-checked={on}
                  // The VALUE, not the label — the test id has to identify what would be written,
                  // because value and label routinely differ and that difference is the bug class
                  // this whole feature had to design around.
                  data-testid={`ctx-crm-category-${opt.value}`}
                  style={{
                    fontSize: 11.5,
                    padding: '3px 9px',
                    borderRadius: 999,
                    cursor: saving || disabled ? 'default' : 'pointer',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                    background: on ? 'var(--accent)' : 'transparent',
                    color: on ? '#fff' : 'var(--text-muted)',
                    opacity: saving || disabled ? 0.6 : 1,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {error && (
            <div
              className="ac-ctx-na"
              data-testid="ctx-crm-category-error"
              style={{ color: 'var(--danger, #c0392b)', marginTop: 6 }}
            >
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
};
